/**
 * DmsMetricsAutoFlusher — DA-2 wire-up.
 *
 * The strictness telemetry counters in `relevanceScoring.ts` /
 * `docApplicability.ts` mutate in-process during normal usage (every
 * Gemini scoring call, every chip render that fires the filename
 * gate). This component is a leaf-level mount that observes those
 * counters on a 5 s timer and arms `scheduleDmsMetricsFlush()` when
 * any counter has changed since the last observation.
 *
 * The debounce in `persistentMetrics.ts` (30 s default) coalesces
 * bursts into one Firestore write — so a heavy doc-matching sweep
 * costs ONE merged setDoc at the end of the activity window.
 *
 * Why a poller rather than instrumenting every counter increment:
 *   1. The hot-path modules stay free of auth/Firestore imports — no
 *      coupling between the scoring engine and the firebase shell.
 *   2. Cold-start cost is one timer setup; the steady state pings
 *      `getDocMatchStrictnessSummary` (a single object literal read)
 *      every 5 s.
 *   3. Survives router transitions — the component mounts once at
 *      the App root, just like AutoSaver.
 *
 * Logged out → no-op (the scheduler also no-ops on falsy uid). The
 * effect re-arms whenever the signed-in uid changes.
 *
 * Test seam: callers can override the polling interval, the
 * `flushNow` invocation, and the summary reader so tests don't need
 * real timers or Firestore.
 */
import { useEffect, useRef } from 'react';
import { auth } from '../firebase';
import { useStore } from '../store';
import {
  scheduleDmsMetricsFlush,
  cancelPendingDmsMetricsFlush,
} from '../services/docMatching/persistentMetrics';
import { getDocMatchStrictnessSummary, type DocMatchStrictnessSummary } from '../services/docMatching/strictnessTelemetry';

/** Default observation interval. Cheap synchronous read so 5 s is fine. */
export const DMS_METRICS_OBSERVATION_INTERVAL_MS = 5_000;

interface DmsMetricsAutoFlusherProps {
  /** Test seam — override the observation interval. */
  intervalMs?: number;
  /** Test seam — override the telemetry summary reader. */
  readSummary?: () => DocMatchStrictnessSummary;
  /** Test seam — override the scheduler invocation. */
  schedule?: typeof scheduleDmsMetricsFlush;
  /** Test seam — override the auth uid lookup. */
  getUid?: () => string | null;
  /**
   * Test seam — override admin detection. `dms_metrics/{day}` is
   * admin-write-only at the rules layer, so a non-admin flush is a
   * guaranteed permission-denied that the helper swallows but still
   * logs as `[dms persistentMetrics] flush failed`. Gating the flush on
   * the admin UI hint makes non-admin sessions the documented silent
   * no-op (firestore.rules: "Non-admin sessions: silent no-op").
   */
  isAdminOverride?: boolean;
}

function summaryFingerprint(s: DocMatchStrictnessSummary): string {
  // Cheap stable hash of the 7 counters. No JSON.stringify allocations
  // when nothing changed — caller compares strings.
  return [
    s.scoring.cacheHits,
    s.scoring.cacheMisses,
    s.scoring.timeouts,
    s.scoring.errors,
    s.scoring.filenameShortCircuits,
    s.applicability.filenameNegativeSuppressed,
    s.applicability.strongClaimRejected,
  ].join(':');
}

export function DmsMetricsAutoFlusher({
  intervalMs = DMS_METRICS_OBSERVATION_INTERVAL_MS,
  readSummary = getDocMatchStrictnessSummary,
  schedule = scheduleDmsMetricsFlush,
  getUid,
  isAdminOverride,
}: DmsMetricsAutoFlusherProps = {}) {
  const lastFingerprint = useRef<string>('');
  // `userRole === 'admin'` is the UI hint AuthGate sets after the
  // admin-claim check resolves. `dms_metrics` is admin-write-only, so a
  // non-admin flush always 403s (swallowed, but logged). Gate on the hint
  // so non-admin sessions never issue the doomed write — the rule remains
  // the canonical guard if the hint is briefly stale.
  const userRole = useStore(s => s.userRole);
  const isAdmin = isAdminOverride ?? (userRole === 'admin');

  useEffect(() => {
    if (!isAdmin) return undefined;
    const uidLookup = getUid ?? (() => auth.currentUser?.uid ?? null);
    // Seed the fingerprint at mount so we don't write zero-valued
    // counters on first observation.
    lastFingerprint.current = summaryFingerprint(readSummary());

    const tick = () => {
      const uid = uidLookup();
      if (!uid) return;
      const snap = readSummary();
      const fp = summaryFingerprint(snap);
      if (fp === lastFingerprint.current) return;
      lastFingerprint.current = fp;
      schedule({ uid });
    };

    const timer = setInterval(tick, intervalMs);
    return () => {
      clearInterval(timer);
      // Don't cancel the pending flush — let it fire so we don't drop
      // telemetry on unmount (HMR, route change, etc.). The scheduler
      // is module-scoped, not component-scoped.
    };
  }, [intervalMs, readSummary, schedule, getUid, isAdmin]);

  // Flush on tab close — best effort. Browser ignores async writes
  // during unload, but Firestore queues the setDoc to IndexedDB and
  // replays on next session, so this is the right pattern here.
  useEffect(() => {
    if (typeof window === 'undefined' || !isAdmin) return undefined;
    const onUnload = () => {
      const uid = (getUid ?? (() => auth.currentUser?.uid ?? null))();
      if (!uid) return;
      const snap = readSummary();
      if (summaryFingerprint(snap) === lastFingerprint.current) return;
      schedule({ uid, debounceMs: 0 });
    };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [getUid, readSummary, schedule, isAdmin]);

  return null;
}

// Re-export for tests that want to assert the scheduler ran.
export { cancelPendingDmsMetricsFlush };
